import { useCallback, useEffect, useRef, useState } from 'react';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
  monitorForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useReduxState } from 'hooks';
import { Avatar, Button } from 'components/common';
import { Icon } from 'components/common/icons';

import type {
  BaseEventPayload,
  ElementDragType
} from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types';
import type { IconProp } from 'components/common/icons/Icon';

type DragEvent = BaseEventPayload<ElementDragType>;
type Item<T> = { key: T; label: string };
type RenderItem<T> = (key: T, index: number) => React.ReactNode;

interface ItemProps<T> {
  data: T[];
  disabled?: boolean;
  dragging?: T;
  identifier: T;
  identifierDisplay?: T;
  includeButtons: boolean;
  index: number;
  listId: string;
  moveItem: (fromIndex: number, toIndex: number) => void;
  moveWithHandle: boolean;
  moveWithHandleIcon: IconProp;
  moveWithHandleStyles?: React.CSSProperties;
  renderItem: RenderItem<T>;
  setDragging: React.Dispatch<React.SetStateAction<T>>;
  styles?: React.CSSProperties;
  withBorder?: boolean;
}

const SortableItem = <T extends string | number>({
  data,
  disabled,
  dragging,
  identifier,
  identifierDisplay,
  includeButtons,
  index,
  listId,
  moveItem,
  moveWithHandle,
  moveWithHandleIcon,
  moveWithHandleStyles,
  renderItem,
  setDragging,
  styles,
  withBorder
}: ItemProps<T>) => {
  const { messages, theme } = useReduxState();

  const elementRef = useRef<HTMLDivElement>(null);

  const [isHandleHovered, setIsHandleHovered] = useState(false);

  const canDrag = !disabled && (!moveWithHandle || isHandleHovered);

  useEffect(() => {
    if (!elementRef.current) return;
    const element = elementRef.current;

    return combine(
      draggable({
        canDrag: () => canDrag,
        element,
        getInitialData: () => ({ identifier, listId }),
        onDragStart: () => setDragging(identifier)
      }),
      dropTargetForElements({
        element,
        getData: () => ({ identifier, listId })
      })
    );
  }, [canDrag, identifier, listId, setDragging]);

  const identifierToShow = identifierDisplay ?? identifier;

  const displayIndex = data.indexOf(identifierToShow);

  let itemStyles = 'item';
  if (withBorder) itemStyles += ' with-border';
  if (dragging === identifierToShow) itemStyles += ' drag-over';
  if (canDrag) itemStyles += ' enabled';

  return (
    <div className={itemStyles} ref={elementRef} style={styles}>
      {moveWithHandle ? (
        <div
          className="handle"
          data-tooltip={messages.commonMove}
          onPointerEnter={() => setIsHandleHovered(true)}
          onPointerLeave={() => setIsHandleHovered(false)}
        >
          <Icon icon={moveWithHandleIcon} style={moveWithHandleStyles} />
        </div>
      ) : null}

      {renderItem(identifierToShow, displayIndex)}

      {includeButtons ? (
        <div className="buttons">
          <Button
            category="light"
            disabled={disabled || index === 0}
            icon="arrow-up"
            isRounded={true}
            onClick={() => moveItem(index, index - 1)}
            size="small"
            tooltip={messages.commonMoveUp}
          />

          <Button
            category="light"
            disabled={disabled || index === data.length - 1}
            icon="arrow-down"
            isRounded={true}
            onClick={() => moveItem(index, index + 1)}
            size="small"
            tooltip={messages.commonMoveDown}
          />
        </div>
      ) : null}

      <style jsx>{`
        .enabled {
          cursor: move;
        }
        .item {
          align-items: center;
          background-color: white;
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.5rem;
          font-weight: 600;
          padding: 12px 15px;
          transition: box-shadow 0.3s;
        }
        .buttons {
          display: flex;
          flex-shrink: 0;
          gap: 5px;
          margin-left: auto;
          padding-left: 10px;
        }
        .with-border:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        .with-border:hover {
          box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.3);
        }
        .drag-over,
        .drag-over:not(:first-child),
        .drag-over:hover,
        .drag-over:not(:first-child):hover {
          border: 2px dashed ${theme.textColorLighter};
          margin: -2px;
          opacity: 0.3;
        }
        .handle {
          background-color: ${theme.lightGreyDark};
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColorLighter};
          cursor: pointer;
          cursor: grab;
          font-size: 1.2rem;
          margin-right: 5px;
          padding: 7px 3px;
          transition: background-color 0.3s;
        }
        .handle:hover {
          background-color: ${theme.lightGreyDarkActive};
        }
      `}</style>
    </div>
  );
};

type Props<T> = {
  disabled?: boolean;
  includeButtons?: boolean;
  itemStyles?: React.CSSProperties;
  itemWithBorder?: boolean;
  listId: string;
  moveWithHandle?: boolean;
  moveWithHandleIcon?: IconProp;
  moveWithHandleStyles?: React.CSSProperties;
  onChange: (keys: T[]) => void;
  syncWithParent?: boolean;
} & (
  | { items: Item<T>[]; renderItem?: never }
  | { items: T[]; renderItem: RenderItem<T> }
);

const SortableList = <T extends string | number>({
  disabled = false,
  includeButtons = false,
  items,
  itemStyles,
  itemWithBorder,
  listId,
  moveWithHandle = false,
  moveWithHandleIcon = 'grip-vertical',
  moveWithHandleStyles,
  onChange,
  renderItem,
  syncWithParent = false
}: Props<T>) => {
  const { theme } = useReduxState();

  const dataRef = useRef<T[]>(
    Boolean(renderItem)
      ? (items as T[])
      : (items as Item<T>[]).map((item) => item.key)
  );

  const [display, setDisplay] = useState<T[]>(dataRef.current);
  const [dragging, setDragging] = useState<T>(null);

  useEffect(() => {
    if (!syncWithParent) return;

    const parentItems = Boolean(renderItem)
      ? (items as T[])
      : (items as Item<T>[]).map((item) => item.key);

    if (JSON.stringify(parentItems) !== JSON.stringify(dataRef.current)) {
      dataRef.current = parentItems;
      setDisplay(parentItems);
    }
  }, [items, syncWithParent]);

  const getReorderedData = useCallback((fromIndex: number, toIndex: number) => {
    const newData = [...dataRef.current];
    const movedKeys = newData.splice(fromIndex, 1);
    newData.splice(toIndex, 0, movedKeys[0]);
    return newData;
  }, []);

  const updateOrderWithDragAndDrop = useCallback(
    ({ location, source }: DragEvent) => {
      const destination = location.current.dropTargets[0];
      if (!destination) return dataRef.current;

      const sourceId = source.data.identifier as T;
      const sourceIndex = dataRef.current.indexOf(sourceId);

      const destinationId = destination.data.identifier as T;
      const destinationIndex = dataRef.current.indexOf(destinationId);
      if (destinationIndex === sourceIndex) return dataRef.current;

      return getReorderedData(sourceIndex, destinationIndex);
    },
    [getReorderedData]
  );

  const moveItemWithButtons = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (toIndex < 0 || toIndex >= dataRef.current.length) return;

      const newData = getReorderedData(fromIndex, toIndex);
      dataRef.current = newData;
      setDisplay(newData);
      onChange(newData);
    },
    [onChange, getReorderedData]
  );

  useEffect(() => {
    return monitorForElements({
      onDrop: (args) => {
        // monitorForElements sets a global listener, so
        // multiple instances of SortableList would all trigger
        // themselves without a unique identifier to distinguish them
        if (args.source?.data?.listId !== listId) return;

        // we only want to trigger onChange if the order
        // has actually been updated in some way
        const updatedOrder = updateOrderWithDragAndDrop(args);
        const isUpdated =
          JSON.stringify(updatedOrder) !== JSON.stringify(dataRef.current);

        dataRef.current = updatedOrder;
        setDragging(null);
        setDisplay(updatedOrder);
        if (isUpdated) onChange(updatedOrder);
      },
      onDropTargetChange: (args) => {
        if (args.source?.data?.listId !== listId) return;
        setDisplay(updateOrderWithDragAndDrop(args));
      }
    });
  }, [listId, onChange, updateOrderWithDragAndDrop]);

  const renderFn =
    renderItem ||
    ((key, index) => (
      <>
        <Avatar
          backgroundColor={theme.lightGreyDark}
          fontSize="1.4rem"
          style={{ flexShrink: 0, marginRight: 10 }}
        >
          {index + 1}
        </Avatar>

        <div style={{ flexGrow: 1, minWidth: 0, paddingTop: '4px' }}>
          {(items as Item<T>[])?.find((item) => item.key === key)?.label}
        </div>
      </>
    ));

  return (
    <div className="monitor">
      {dataRef.current.map((key, index) => (
        <SortableItem
          data={dataRef.current}
          disabled={disabled}
          dragging={dragging}
          identifier={key}
          identifierDisplay={display[index]}
          includeButtons={includeButtons}
          index={index}
          key={key}
          listId={listId}
          moveItem={moveItemWithButtons}
          moveWithHandle={moveWithHandle}
          moveWithHandleIcon={moveWithHandleIcon}
          moveWithHandleStyles={moveWithHandleStyles}
          renderItem={renderFn}
          setDragging={setDragging}
          styles={itemStyles}
          withBorder={itemWithBorder}
        />
      ))}
    </div>
  );
};

export default SortableList;
